import { useCallback } from "react"
import { UseFormReturn } from "react-hook-form"
import type { BetterDocUsageEvaluation } from "@/modules/patients/detail/better-doc/lib/fetch-better-doc-usage-for-consultation"
import { isBetterDocRequired } from "@/modules/patients/detail/better-doc/lib/is-better-doc-required"
import {
  hasBetterDocUsageBecomeRequired,
  hasCareProviderSelectionBecomeUnblocked,
} from "@/modules/patients/detail/better-doc/lib/point-of-care-better-doc-rules"
import type { PointOfCareWithBetterDoc } from "@/modules/patients/detail/better-doc/types"
import { PointOfCareFormType } from "@/modules/patients/detail/progress-note/point-of-care/schema"
import { useReferralStoreContext } from "@/modules/patients/detail/referral/store/useReferralStore"
import type { PointOfCareType } from "@/types/__generated__/union-types"

type UseChBetterDocCareProviderSelectionParameters = {
  form: UseFormReturn<PointOfCareFormType>
  effectivePointOfCareWithBetterDoc: PointOfCareWithBetterDoc | null
  specialty: PointOfCareFormType["specialtyId"]
  clearBetterDocSuggestion: () => void
  preselectCareProviderForPointOfCare: (selectedPointOfCare: PointOfCareType | undefined) => void
  preselectCareProviderForSpecialty: (specialtyId: string | undefined) => void
}

type ChBetterDocCareProviderSelection = {
  clearSelectedCareProvider: () => void
  updateCareProviderAfterBetterDocUsageEvaluation: (
    previousSuccessfulEvaluation: BetterDocUsageEvaluation | null,
    betterDocUsageEvaluation: BetterDocUsageEvaluation,
  ) => void
}

export const useChBetterDocCareProviderSelection = ({
  form,
  effectivePointOfCareWithBetterDoc,
  specialty,
  clearBetterDocSuggestion,
  preselectCareProviderForPointOfCare,
  preselectCareProviderForSpecialty,
}: UseChBetterDocCareProviderSelectionParameters): ChBetterDocCareProviderSelection => {
  const { setValue } = form
  const setReferralCareProvider = useReferralStoreContext((state) => state.setCareProvider)

  const clearSelectedCareProvider = useCallback((): void => {
    setValue("careProviderId", null, { shouldDirty: true, shouldValidate: true })
    clearBetterDocSuggestion()
  }, [clearBetterDocSuggestion, setValue])

  const preselectCareProviderWhenSelectionBecameUnblocked = (
    previousSuccessfulEvaluation: BetterDocUsageEvaluation | null,
    betterDocUsageEvaluation: BetterDocUsageEvaluation,
  ): void => {
    const isCareProviderSelectionUnblocked = hasCareProviderSelectionBecomeUnblocked(
      previousSuccessfulEvaluation?.missingConsultationDetailsThatCanChangeBetterDocUsage ?? null,
      betterDocUsageEvaluation.missingConsultationDetailsThatCanChangeBetterDocUsage,
    )
    if (!isCareProviderSelectionUnblocked || isBetterDocRequired(betterDocUsageEvaluation.betterDocUsage)) return

    if (effectivePointOfCareWithBetterDoc === "GENERAL_PRACTITIONER") {
      preselectCareProviderForPointOfCare("GENERAL_PRACTITIONER")
    } else if (effectivePointOfCareWithBetterDoc === "SPECIALIST") {
      preselectCareProviderForSpecialty(specialty?.specialty?.id)
    }
  }

  const updateCareProviderAfterBetterDocUsageEvaluation = (
    previousSuccessfulEvaluation: BetterDocUsageEvaluation | null,
    betterDocUsageEvaluation: BetterDocUsageEvaluation,
  ): void => {
    if (
      hasBetterDocUsageBecomeRequired(
        previousSuccessfulEvaluation?.betterDocUsage ?? null,
        betterDocUsageEvaluation.betterDocUsage,
      )
    ) {
      clearSelectedCareProvider()
      setReferralCareProvider(null)
    }
    preselectCareProviderWhenSelectionBecameUnblocked(previousSuccessfulEvaluation, betterDocUsageEvaluation)
  }

  return { clearSelectedCareProvider, updateCareProviderAfterBetterDocUsageEvaluation }
}
